import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import UploadButton from "@/components/UploadButton";
import {
  X, Plus, Trash2, Lock, Pencil, MoreHorizontal, MessageSquare, Pin,
} from "lucide-react";

export const STATUS_LABELS = {
  considering: "Considering", contacted: "Contacted", quote_received: "Quote received",
  hired: "Hired", completed: "Completed", not_selected: "Not selected",
};
export const MEDIA_KIND_LABELS = {
  inspiration: "Inspiration", before: "Before photo", pro_work: "Pro's work",
  product: "Product", other: "Other",
};

const pinStyle = "absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#3f7a63]/70";

/* ---------- small shared pieces ---------- */

const CardMenu = ({ items, testid }) => {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div className="absolute top-2 right-2 z-20">
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className="w-7 h-7 rounded-full bg-white/95 shadow-sm grid place-items-center text-[#222]/60 hover:text-[#222] transition"
        data-testid={testid}
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={e => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute right-0 mt-1 z-20 bg-white rounded-xl shadow-lg border border-black/5 py-1 min-w-[150px]">
            {items.map(it => (
              <button
                key={it.label}
                onClick={e => { e.stopPropagation(); setOpen(false); it.onClick(); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 ${it.danger ? "text-red-600" : ""}`}
                data-testid={it.testid}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CommentBadge = ({ count, onClick, testid }) => count > 0 ? (
  <button onClick={onClick} className="inline-flex items-center gap-1 text-[11.5px] font-bold text-white bg-[#2E6B57] hover:bg-[#245544] rounded-full px-2 py-0.5 transition-colors" data-testid={testid}>
    <MessageSquare size={11} /> {count}
  </button>
) : null;

/* ---------- board cards ---------- */

export const PhotoCard = ({ m, canManage, comments, onDelete, onEditCaption, onComment, canComment }) => (
  <div className="relative rounded-xl overflow-hidden bg-white border border-black/5 shadow-[0_2px_6px_rgba(60,45,25,0.08)] hover:shadow-[0_8px_22px_rgba(60,45,25,0.14)] transition-shadow" data-testid={`board-photo-${m.id}`}>
    <img src={m.url} alt={m.caption || ""} className="w-full aspect-[4/3] object-cover" />
    {canManage && (
      <CardMenu
        testid={`board-photo-menu-${m.id}`}
        items={[
          { label: "Edit caption", onClick: () => onEditCaption(m), testid: `board-photo-caption-${m.id}` },
          { label: "Delete photo", onClick: () => onDelete(m.id), danger: true, testid: `board-photo-delete-${m.id}` },
        ]}
      />
    )}
    <div className="px-2.5 py-2 flex items-center gap-2 flex-wrap">
      {m.kind && (
        <span className="text-[10px] font-semibold text-[#245544] bg-[#E9F0EC] rounded-full px-2 py-0.5 shrink-0">
          {MEDIA_KIND_LABELS[m.kind] || m.kind}
        </span>
      )}
      {m.caption && <p className="text-xs text-[#222]/75 flex-1 min-w-0 truncate">{m.caption}</p>}
      <div className="ml-auto flex items-center gap-1.5">
        <CommentBadge count={comments.length} onClick={() => onComment(m)} testid={`board-photo-comments-${m.id}`} />
        {canComment && comments.length === 0 && (
          <button onClick={() => onComment(m)} className="text-[#222]/45 hover:text-[#245544]" data-testid={`board-photo-comment-btn-${m.id}`}>
            <MessageSquare size={13} />
          </button>
        )}
      </div>
    </div>
  </div>
);

export const NoteCard = ({ n, canManage, comments, onEdit, onDelete, onComment, canComment, onZoom }) => (
  <div className="relative rounded-xl bg-[#FDF8EC] border border-[#E4D6B8] shadow-[0_2px_6px_rgba(60,45,25,0.08)] hover:shadow-[0_8px_22px_rgba(60,45,25,0.14)] transition-shadow px-4 pt-5 pb-3.5 min-h-[124px] flex flex-col" data-testid={`board-note-${n.id}`}>
    <span className={pinStyle} aria-hidden="true" />
    {canManage && (
      <CardMenu
        testid={`board-note-menu-${n.id}`}
        items={[
          { label: "Edit note", onClick: () => onEdit(n), testid: `board-note-edit-${n.id}` },
          { label: "Unpin from board", onClick: () => onEdit({ ...n, _unpin: true }), testid: `board-note-unpin-${n.id}` },
          { label: "Delete note", onClick: () => onDelete(n.id), danger: true, testid: `board-note-delete-${n.id}` },
        ]}
      />
    )}
    {n.image_url && (
      <button onClick={() => onZoom?.(n.image_url)} className="block -mx-1 mb-2.5 rounded-lg overflow-hidden" data-testid={`board-note-image-${n.id}`}>
        <img src={n.image_url} alt="" className="w-full aspect-[4/3] object-cover" />
      </button>
    )}
    <p className="text-[13.5px] leading-snug text-[#3d3223] flex-1 whitespace-pre-wrap" style={{ fontFamily: "Outfit" }}>{n.text}</p>
    <div className="flex items-center gap-1.5 mt-2.5">
      {n.visibility === "owner_only" && (
        <span className="text-[10px] text-[#7a6a4f] flex items-center gap-1"><Lock size={9} /> Private</span>
      )}
      <div className="ml-auto flex items-center gap-1.5">
        <CommentBadge count={comments.length} onClick={() => onComment(n)} testid={`board-note-comments-${n.id}`} />
        {canComment && comments.length === 0 && (
          <button onClick={() => onComment(n)} className="text-[#7a6a4f]/70 hover:text-[#245544]" data-testid={`board-note-comment-btn-${n.id}`}>
            <MessageSquare size={13} />
          </button>
        )}
      </div>
    </div>
  </div>
);

export const ProCard = ({ m, canManage, comments, onStatus, onNote, onRemove, onComment, canComment }) => (
  <div className="relative rounded-xl bg-white border border-black/8 shadow-[0_2px_6px_rgba(60,45,25,0.08)] hover:shadow-[0_8px_22px_rgba(60,45,25,0.14)] transition-shadow p-4" data-testid={`board-pro-${m.pro_user_id}`}>
    {canManage && (
      <CardMenu
        testid={`board-pro-menu-${m.pro_user_id}`}
        items={[
          { label: m.note ? "Edit private note" : "Add private note", onClick: () => onNote(m), testid: `board-pro-note-btn-${m.pro_user_id}` },
          { label: "Remove from project", onClick: () => onRemove(m.pro_user_id), danger: true, testid: `board-pro-remove-${m.pro_user_id}` },
        ]}
      />
    )}
    <div className="w-11 h-11 rounded-full bg-[#2E6B57] grid place-items-center text-sm font-bold text-white overflow-hidden mb-2.5">
      {m.pro?.avatar_url ? <img src={m.pro.avatar_url} className="w-full h-full object-cover" alt="" /> : (m.pro?.business_name?.[0] || "?")}
    </div>
    <p className="text-[13.5px] font-bold leading-tight truncate" style={{ fontFamily: "Outfit" }}>{m.pro?.business_name || "Unavailable pro"}</p>
    <p className="text-xs text-[#222]/50 truncate mb-2.5">{m.pro?.category}</p>
    {canManage ? (
      <select
        value={m.status}
        onChange={e => onStatus(m.pro_user_id, e.target.value)}
        className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EEF2EF] text-[#245544] border-0 outline-none cursor-pointer max-w-full"
        data-testid={`board-pro-status-${m.pro_user_id}`}
      >
        {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    ) : (
      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EEF2EF] text-[#245544] inline-block" data-testid={`board-pro-status-label-${m.pro_user_id}`}>
        {STATUS_LABELS[m.status] || m.status}
      </span>
    )}
    {canManage && m.note && (
      <button onClick={() => onNote(m)} className="mt-2 w-full text-left text-[11px] text-[#222]/55 bg-black/[0.03] rounded-lg px-2 py-1.5 truncate hover:bg-black/[0.06]" data-testid={`board-pro-note-preview-${m.pro_user_id}`}>
        <Lock size={9} className="inline mr-1 -mt-0.5" />{m.note}
      </button>
    )}
    <div className="flex items-center gap-1.5 mt-2">
      <CommentBadge count={comments.length} onClick={() => onComment(m)} testid={`board-pro-comments-${m.pro_user_id}`} />
      {canComment && comments.length === 0 && (
        <button onClick={() => onComment(m)} className="text-[#222]/45 hover:text-[#245544]" data-testid={`board-pro-comment-btn-${m.pro_user_id}`}>
          <MessageSquare size={13} />
        </button>
      )}
    </div>
  </div>
);

/* ---------- add-photo dialog ---------- */

export const AddPhotoDialog = ({ pros, onCancel, onSave }) => {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [kind, setKind] = useState("inspiration");
  const [proId, setProId] = useState("");
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] grid place-items-center p-4" onClick={e => { e.stopPropagation(); onCancel(); }}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3" onClick={e => e.stopPropagation()} data-testid="add-photo-dialog">
        <h4 className="font-bold" style={{ fontFamily: "Outfit" }}>Add a photo</h4>
        {url
          ? <img src={url} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" />
          : <UploadButton onUploaded={setUrl} label="Choose photo" />}
        <div>
          <p className="text-xs font-semibold mb-1.5">What is it? <span className="text-[#222]/40 font-normal">(optional)</span></p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(MEDIA_KIND_LABELS).map(([k, l]) => (
              <button key={k} onClick={() => setKind(kind === k ? "" : k)}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition ${kind === k ? "bg-[#E9F0EC] border-[#2E6B57] text-[#245544] font-semibold" : "bg-white border-black/10 text-[#222]/60"}`}
                data-testid={`add-photo-kind-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        <input value={caption} onChange={e => setCaption(e.target.value)} maxLength={200} placeholder="Caption (optional)"
          className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="add-photo-caption" />
        {pros.length > 0 && (
          <select value={proId} onChange={e => setProId(e.target.value)} className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm bg-white" data-testid="add-photo-pro">
            <option value="">Not linked to a pro</option>
            {pros.map(p => <option key={p.pro_user_id} value={p.pro_user_id}>{p.pro?.business_name}</option>)}
          </select>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={e => { e.stopPropagation(); onCancel(); }} className="px-4 py-2 text-sm text-[#222]/60" data-testid="add-photo-cancel">Cancel</button>
          <button onClick={() => onSave({ url, caption, kind: kind || null, pro_user_id: proId || null })} disabled={!url}
            className="btn-lime px-5 py-2 text-sm disabled:opacity-40" data-testid="add-photo-save">Add to board</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- add/edit note dialog ---------- */

export const NoteDialog = ({ note, isOwner, onCancel, onSave }) => {
  const [text, setText] = useState(note?.text || "");
  const [pinned, setPinned] = useState(!!note?.pinned);
  const [ownerOnly, setOwnerOnly] = useState(note ? note.visibility === "owner_only" : false);
  const [imageUrl, setImageUrl] = useState(note?.image_url || "");
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] grid place-items-center p-4 overflow-y-auto" onClick={e => { e.stopPropagation(); onCancel(); }}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3 my-auto" onClick={e => e.stopPropagation()} data-testid="note-dialog">
        <h4 className="font-bold" style={{ fontFamily: "Outfit" }}>{note ? "Edit note" : "Add a note"}</h4>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={2000} autoFocus
          placeholder="Ask about quartz edge options…"
          className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="note-dialog-text" />
        {imageUrl ? (
          <div className="relative" data-testid="note-dialog-image-preview">
            <img src={imageUrl} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" />
            <button onClick={() => setImageUrl("")}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 shadow grid place-items-center text-[#222]/60 hover:text-red-600"
              title="Remove photo" data-testid="note-dialog-image-remove"><X size={14} /></button>
          </div>
        ) : (
          <UploadButton onUploaded={setImageUrl} label="Attach a photo" className="w-full justify-center" />
        )}
        <label className="flex items-center gap-2 text-xs text-[#222]/70 cursor-pointer">
          <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} data-testid="note-dialog-pinned" />
          <Pin size={12} /> Pin to Project Board
        </label>
        {isOwner && (
          <label className="flex items-center gap-2 text-xs text-[#222]/70 cursor-pointer">
            <input type="checkbox" checked={ownerOnly} onChange={e => setOwnerOnly(e.target.checked)} data-testid="note-dialog-private" />
            <Lock size={12} /> Private — only visible to me
          </label>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={e => { e.stopPropagation(); onCancel(); }} className="px-4 py-2 text-sm text-[#222]/60" data-testid="note-dialog-cancel">Cancel</button>
          <button onClick={() => onSave({ text: text.trim(), pinned, image_url: imageUrl, visibility: ownerOnly ? "owner_only" : "collaborators" })}
            disabled={!text.trim() && !imageUrl} className="btn-lime px-5 py-2 text-sm disabled:opacity-40" data-testid="note-dialog-save">
            {note ? "Save" : "Add note"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------- item comment thread ---------- */

export const CommentThread = ({ target, comments, canComment, onCancel, onPost }) => {
  const [text, setText] = useState("");
  const label = target._t === "media" ? "photo" : target._t === "note" ? "note" : "professional";
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] grid place-items-center p-4" onClick={e => { e.stopPropagation(); onCancel(); }}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="comment-thread">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold" style={{ fontFamily: "Outfit" }}>Comments on this {label}</h4>
          <button onClick={e => { e.stopPropagation(); onCancel(); }} className="p-1 rounded-full hover:bg-black/5" data-testid="comment-thread-close"><X size={15} /></button>
        </div>
        <div className="space-y-2.5 max-h-56 overflow-y-auto mb-3">
          {comments.length === 0 && <p className="text-sm text-[#222]/40">No comments yet.</p>}
          {comments.map(c => (
            <div key={c.id} className="bg-[#F2F4F2] rounded-xl px-3 py-2" data-testid={`comment-${c.id}`}>
              <p className="text-sm whitespace-pre-wrap">{c.text}</p>
              <p className="text-[11px] text-[#222]/45 mt-0.5">{c.author_name} · {new Date(c.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
        {canComment ? (
          <div className="flex gap-2">
            <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a comment…"
              onKeyDown={e => { if (e.key === "Enter" && text.trim()) { onPost(text.trim()); setText(""); } }}
              className="flex-1 px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]" data-testid="comment-thread-input" />
            <button onClick={() => { if (text.trim()) { onPost(text.trim()); setText(""); } }} className="btn-lime px-4 py-2 text-sm" data-testid="comment-thread-post">Post</button>
          </div>
        ) : (
          <p className="text-xs text-[#222]/45" data-testid="comment-thread-readonly">You have view-only access to this project.</p>
        )}
      </div>
    </div>
  );
};

/* ---------- simple image lightbox ---------- */

export const ImageLightbox = ({ url, onClose }) => (
  <div className="fixed inset-0 bg-black/80 z-[70] grid place-items-center p-4" onClick={e => { e.stopPropagation(); onClose(); }} data-testid="note-image-lightbox">
    <img src={url} alt="" className="max-h-[88vh] max-w-full rounded-xl" onClick={e => e.stopPropagation()} />
    <button onClick={e => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 grid place-items-center" data-testid="note-image-lightbox-close"><X size={17} /></button>
  </div>
);

/* ---------- notes sidebar list ---------- */

export const NotesSidebar = ({ notes, canManage, isOwner, onAdd, onEdit, onDelete, onZoom, title = "Project notes", emptyText = "No notes yet.", listClass = "" }) => (
  <div className="flex flex-col min-h-0" data-testid="project-notes-sidebar">
    <div className="flex items-center justify-between mb-2.5 shrink-0">
      <h4 className="text-[13px] font-bold" style={{ fontFamily: "Outfit" }}>{title}</h4>
      {notes.length > 0 && <span className="text-[11px] text-[#222]/40" data-testid="notes-count">{notes.length}</span>}
    </div>
    {canManage && (
      <button onClick={onAdd} className="w-full mb-3 shrink-0 py-2.5 rounded-xl bg-[#EEF2EF] hover:bg-[#E3EAE5] text-[#245544] text-[13px] font-semibold flex items-center justify-center gap-1.5 transition" data-testid="sidebar-add-note">
        <Plus size={14} /> Add note
      </button>
    )}
    <div className={`space-y-2 ${listClass}`} data-testid="project-notes-list">
      {notes.length === 0 && <p className="text-xs text-[#222]/40">{emptyText}</p>}
      {notes.map(n => (
        <div key={n.id} className="group bg-[#FDF8EC] border border-[#EADFC6] rounded-xl px-3 py-2.5" data-testid={`sidebar-note-${n.id}`}>
          <div className="flex items-start gap-2">
            {n.image_url && (
              <button onClick={() => onZoom?.(n.image_url)} className="w-11 h-11 rounded-lg overflow-hidden shrink-0 border border-[#E4D6B8]" data-testid={`sidebar-note-image-${n.id}`}>
                <img src={n.image_url} alt="" className="w-full h-full object-cover" />
              </button>
            )}
            <p className="text-[13px] leading-snug text-[#3d3223] flex-1 whitespace-pre-wrap">{n.text}</p>
            {canManage && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                <button onClick={() => onEdit(n)} className="text-[#7a6a4f] hover:text-[#245544]" data-testid={`sidebar-note-edit-${n.id}`}><Pencil size={11} /></button>
                <button onClick={() => onDelete(n.id)} className="text-[#7a6a4f] hover:text-red-500" data-testid={`sidebar-note-delete-${n.id}`}><Trash2 size={11} /></button>
              </div>
            )}
          </div>
          {(n.visibility === "owner_only" || n.pinned) && (
            <p className="text-[10px] text-[#7a6a4f] mt-1.5 flex items-center gap-1">
              {n.visibility === "owner_only" && <><Lock size={9} /> Private</>}
              {n.visibility === "owner_only" && n.pinned && <span>·</span>}
              {n.pinned && <><Pin size={9} /> On board</>}
            </p>
          )}
        </div>
      ))}
    </div>
    {isOwner && <p className="text-[10px] text-[#222]/35 mt-2 shrink-0">Private notes stay yours. Shared notes appear for collaborators.</p>}
  </div>
);
